import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import { createSelector } from "reselect";
import { pinIcon, taskIcon, unPinIcon } from "shared/icons/svgHelpers";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import { ThemeType } from "shared/styles/theme";
import colors from "shared/styles/colors";
import { DispatchType } from "shared/utilities";
import { toggleEditTask } from "processEditor/actions/SidePanelActions";
import { State } from "processEditor/model/State";
import AssistantModalLauncher from "./AssistantModalLauncher";
import { PromptType } from "./Modals/AssistantModal";
import { ElementType } from "processEditor/model/IProcessData";
import { ExpressionParameters } from "./ExpressionEditor";
import styled from "styled-components";

export const ParameterEditorContainerId = "process-element-parameter-content-container";
export const TestProcessParameterEditorContainerId = "test-process-element-parameter-content-container";

const SidePanelContent: React.FC<React.PropsWithChildren<SidePanelContentProps>> = (props) => {
    const [theme] = useUserTheme();
    const handleCloseButton = () => {
        props.onCloseButton();
    }

    const handlePinButton = () => {
        if (props.onPinButton) {
            props.onPinButton();
        }
    }

    const handleToggleClickDocumentationButton = () => {
        if (props.toggleClickDocumentationButton) {
            props.toggleClickDocumentationButton();
        }
    }

    const handleToggleEditTask = () => {
        props.onEditTask(!props.isEditingTask)
    }

    const iconColor = theme === ThemeType.light ? colors.black : colors.grey_4;
    const panelIconStyles: React.CSSProperties = {
        fontSize: "1.5rem"
    }
    const editIconsStyles: React.CSSProperties = {
        ...panelIconStyles,
        color: theme === ThemeType.light ? colors.purplePrimary : colors.purpleTertiary
    }

    return (
        <StyledSidePanelContainer style={{ overflowY: "hidden" }}>
            <S.ProcessElementParameterHeader $isPanelPinned={props.isPanelPinned} className="process-element-parameter-header-selector">
                <S.FlexContainer centerItems={true} justifySpaceBetween={true} noWrap={true}>
                    {props.currentTask && props.isTaskPanel ? (
                        <S.FlexContainer gap={10}>
                            <S.FlexContainer gap={10} noWrap={true}>
                                <div style={{ alignSelf: "center" }} dangerouslySetInnerHTML={{ __html: taskIcon(iconColor) }} />
                                <h2 className="task-name-selector" style={{ wordBreak: "break-all" }}>{props.currentTask.elementName}</h2>
                            </S.FlexContainer>
                            <S.FlexContainer gap={10} style={{ alignItems: "baseline" }}>
                                <S.SmallText className="package-name-version-selector">{props.currentTask.packageNameWithVersion}</S.SmallText>
                                <i className="ci-edit pointer" style={editIconsStyles} title="Edit element name and change Task" onClick={handleToggleEditTask} />
                            </S.FlexContainer>
                        </S.FlexContainer>
                    ) : (
                        <h2>{props.title}</h2>
                    )}
                    <S.FlexContainer centerItems={true} style={{ flexWrap: "nowrap", alignSelf: "start" }}>
                        {props.toggleClickDocumentationButton && (
                            <span onClick={handleToggleClickDocumentationButton} title={"Documentation"} className="pointer">
                                <i className="ci-file_blank_outline" style={panelIconStyles}></i>
                            </span>
                        )}
                        {props.isPanelPinned != null ?
                            <span onClick={handlePinButton} className="pointer" style={{ marginLeft: 7 }}>
                                {props.isPanelPinned ?
                                    <div dangerouslySetInnerHTML={{ __html: unPinIcon(iconColor) }} title={FRENDS.Localizations.Unpin} />
                                    : <div dangerouslySetInnerHTML={{ __html: pinIcon(iconColor) }} title={FRENDS.Localizations.Pin} />
                                }
                            </span> : null}
                        <span onClick={handleCloseButton} title={FRENDS.Localizations.Close} className="pointer" style={{ marginLeft: 7 }}>
                            <i className="ci-close_big" style={panelIconStyles}></i>
                        </span>
                    </S.FlexContainer>
                </S.FlexContainer>
            </S.ProcessElementParameterHeader>
            <StyledSidePanelContainer id={props.containerId} style={{ overflowY: "auto" }}>
                {props.children}
            </StyledSidePanelContainer>
            {props.elementId && props.promptType &&
                <div style={{ padding: "24px", alignSelf: "end" }}>
                    <AssistantModalLauncher elementId={props.elementId} type={props.promptType} />
                </div>
            }
        </StyledSidePanelContainer>
    );
}

const StyledSidePanelContainer = styled.div`
    display: flex;
    flex-direction: column ;
    flex-grow: 1;
`;

interface ICurrentTask {
    elementId: string;
    elementName?: string;
    packageNameWithVersion: string;
}

interface SidePanelContentStateProps {
    currentTask?: ICurrentTask;
    isEditingTask?: boolean;
    isTaskPanel?: boolean;
    elementId?: string;
    promptType?: PromptType | null;
}

interface SidePanelContentOwnProps {
    onCloseButton: () => void;
    isPanelPinned?: boolean; // Also used to determine if panel is pinnable or not.
    onPinButton?: () => void;
    title: string | undefined | null;
    containerId: string;
    toggleClickDocumentationButton?: () => void;
}

interface SidePanelContentDispatchProps {
    onEditTask: (show: boolean) => void;
}

const getElement = (state: State) => state.uiState.panelSettings?.elementPanel.selectedElementId ?
    state.elements[state.uiState.panelSettings.elementPanel.selectedElementId] : null;

const getTasks = (state: State) => state.tasks;
export const getCurrentTask = createSelector(
    [getElement, getTasks],
    (element, tasks) => {
        if (element?.selectedTypeId) {
            const currentTask = tasks.find((task) => task.uri === element.selectedTypeId);
            if (currentTask === undefined) {
                return undefined;
            }
            // Find the version string from package name and append it to the package name
            const packageVersion = currentTask.packageName.slice(currentTask.packageName.search(/(\d+\.)?(\d+\.)?(\d+)/g));
            const currentTaskNameWithPackage: ICurrentTask = {
                elementName: element.name,
                elementId: element.id,
                packageNameWithVersion: `${currentTask.name} ${packageVersion}`
            }
            return currentTaskNameWithPackage;
        }
    }
);

export type SidePanelContentProps = SidePanelContentOwnProps & SidePanelContentStateProps & SidePanelContentDispatchProps;

const mapStateToProps = (state: State, ownProps: SidePanelContentOwnProps): SidePanelContentStateProps => {
    const currentTask = getCurrentTask(state);
    const currentElement = getElement(state);
    let promptType: PromptType | null = null;
    const isElementPanel = ownProps.containerId === ParameterEditorContainerId;

    if (isElementPanel) {
        switch (currentElement?.type) {
            case ElementType.Expression: {
                const elemParameters = currentElement.parameters as ExpressionParameters;
                const inStatementMode = elemParameters.useStatementMode && elemParameters.useStatementMode.value || false;
                if (inStatementMode) {
                    promptType = PromptType.CodeCompletion;
                }
            }

            case ElementType.Task: {
                if (currentElement.selectedTypeId) {
                    promptType = PromptType.TaskCompletion;
                }
            }
        }
    }
    return {
        currentTask: currentTask,
        isEditingTask: state.uiState.isEditingTask,
        isTaskPanel: isElementPanel,
        elementId: currentElement?.id,
        promptType: promptType
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): SidePanelContentDispatchProps => {
    return {
        onEditTask: (show: boolean) => dispatch(toggleEditTask(show))
    };
};


export default connect(mapStateToProps, mapDispatchToProps)(SidePanelContent);